/**
 * Generic FK picker — replaces UUID textboxes throughout the app.
 *
 * Implementation note: built with a plain TextInput + a manually-positioned
 * Paper dropdown (instead of Mantine's Combobox primitive). The Combobox
 * had focus/blur fights with the TextInput target that prevented the
 * dropdown from staying open long enough to click an option. The simpler
 * pattern below is more code but actually works.
 */
import {
  ActionIcon, Loader, Paper, Stack, Text, TextInput, UnstyledButton,
} from '@mantine/core';
import { useDebouncedValue } from '@mantine/hooks';
import { IconChevronDown, IconX } from '@tabler/icons-react';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';

import { accountInsuranceApi } from '../api/accountInsurance';
import { accountsApi } from '../api/accounts';
import { agreementPartiesApi } from '../api/agreementParties';
import { agreementsApi } from '../api/agreements';
import { personsApi } from '../api/persons';
import type { LifecycleStatus, Page, UUID } from '../api/types';

// ---------------------------------------------------------------------------
// Generic core
// ---------------------------------------------------------------------------
export interface EntityPickerProps<T extends { id: UUID }> {
  label?: string;
  placeholder?: string;
  value: UUID | null | undefined;
  onChange: (id: UUID | null) => void;
  search: (q: string) => Promise<Page<T>>;
  getById: (id: UUID) => Promise<T>;
  formatLabel: (row: T) => string;
  formatDetail?: (row: T) => string | undefined;
  required?: boolean;
  disabled?: boolean;
  description?: string;
  error?: string;
  unstyled?: boolean;
  /** Mantine input size for the trigger field (default 'sm'). Filter rows
   *  pass 'xs' to match the compact ColumnXxxFilter controls. */
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  /** Path used for the empty-state link ("No agreements yet — create one first"). */
  createPath?: string;
  /** Friendly noun for the empty-state hint (e.g. "agreement"). */
  entityNoun?: string;
  /** D2-polish 2: when set, renders a "[+ Create new {entityNoun}]" option at
   *  the top of the dropdown. Calling code is responsible for navigating to
   *  the create form with the appropriate return_to query so the new entity's
   *  id flows back into this picker on return. */
  onCreateNew?: () => void;
  /** Filter use: fixed options rendered ABOVE the live search (e.g. "All",
   *  "General"). Selecting one calls onChange(value) with the sentinel the
   *  caller interprets. Omitting it leaves the picker's behaviour unchanged. */
  pinnedOptions?: { value: string; label: string }[];
}

export function EntityPicker<T extends { id: UUID }>({
  label, placeholder, value, onChange, search, getById, formatLabel, formatDetail,
  required, disabled, description, error, createPath, entityNoun, onCreateNew,
  pinnedOptions, size,
}: EntityPickerProps<T>) {
  const [searchInput, setSearchInput] = useState('');
  const [debouncedSearch] = useDebouncedValue(searchInput, 200);
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // A pinned (filter sentinel) value isn't a real entity id — don't resolve
  // it via getById; show its supplied label instead.
  const pinnedMatch = value ? pinnedOptions?.find((o) => o.value === value) : undefined;

  // Resolve the friendly label for the current value (if any).
  const currentQ = useQuery<T | null>({
    queryKey: ['entityPicker', label, 'current', value],
    queryFn: async () => (value ? await getById(value) : null),
    enabled: !!value && !pinnedMatch,
    staleTime: 30_000,
  });

  // Search results — fetched whenever the dropdown is open.
  const searchQ = useQuery<Page<T>>({
    queryKey: ['entityPicker', label, 'search', debouncedSearch],
    queryFn: () => search(debouncedSearch),
    enabled: isOpen,
    staleTime: 5_000,
  });

  // External value clear → reset the search.
  useEffect(() => {
    if (!value) setSearchInput('');
  }, [value]);

  // Close the dropdown when the user clicks outside.
  useEffect(() => {
    if (!isOpen) return;
    function onDocClick(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    }
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [isOpen]);

  const selectedLabel = pinnedMatch ? pinnedMatch.label
    : (currentQ.data ? formatLabel(currentQ.data) : '');
  const displayValue = isOpen
    ? searchInput
    : value
      ? (currentQ.isLoading && !pinnedMatch ? 'Loading…' : selectedLabel)
      : '';

  function commit(rowId: UUID) {
    onChange(rowId);
    setSearchInput('');
    setIsOpen(false);
  }

  function clear() {
    onChange(null);
    setSearchInput('');
    setIsOpen(false);
  }

  const items = searchQ.data?.items ?? [];

  return (
    <div ref={containerRef} style={{ position: 'relative' }}>
      <TextInput
        label={label}
        size={size}
        required={required}
        disabled={disabled}
        description={description}
        error={error}
        placeholder={placeholder ?? (value ? '' : 'Search…')}
        value={displayValue}
        onChange={(e) => {
          setSearchInput(e.currentTarget.value);
          if (!isOpen) setIsOpen(true);
        }}
        onFocus={() => { if (!disabled) setIsOpen(true); }}
        // #6 (2026-06-17): reopen the list on CLICK, not only on focus. After
        // committing a selection the option's onMouseDown preventDefault keeps
        // the input focused, so a subsequent click didn't re-fire onFocus and
        // the list stayed shut — forcing a clear (✕) + retype to re-pick.
        // onClick fires regardless of focus state, so clicking the field now
        // reopens the options. Fixes every picker (Agreement/Person/Account/…).
        onClick={() => { if (!disabled) setIsOpen(true); }}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();   // never submit the surrounding form
            // Optional: pick the first item on Enter
            if (isOpen && items.length > 0) commit(items[0].id);
          } else if (e.key === 'Escape') {
            setIsOpen(false);
          } else if (e.key === 'ArrowDown' && !isOpen) {
            setIsOpen(true);
          }
        }}
        rightSection={
          value && !disabled ? (
            <ActionIcon
              variant="subtle" color="gray" size="sm"
              onMouseDown={(e) => {
                e.preventDefault();
                e.stopPropagation();
                clear();
              }}
              title="Clear"
            ><IconX size={14} /></ActionIcon>
          ) : (
            <IconChevronDown size={14} style={{ opacity: 0.5 }} />
          )
        }
        rightSectionPointerEvents={value && !disabled ? 'auto' : 'none'}
      />

      {isOpen && !disabled && (
        <Paper
          shadow="md"
          withBorder
          p="xs"
          style={{
            position: 'absolute',
            top: 'calc(100% + 4px)',
            left: 0,
            right: 0,
            zIndex: 1000,
            maxHeight: 320,
            overflowY: 'auto',
          }}
        >
          <Stack gap={2}>
            {/* D2-polish 2: synthetic "+ Create new" row at the top of the
                dropdown, when the parent supplied a handler. */}
            {onCreateNew && (
              <UnstyledButton
                onMouseDown={(e) => {
                  e.preventDefault();
                  setIsOpen(false);
                  onCreateNew();
                }}
                style={{
                  padding: '6px 10px',
                  borderRadius: 4,
                  width: '100%',
                  textAlign: 'left',
                  borderBottom: '1px solid var(--mantine-color-gray-2)',
                  marginBottom: 4,
                }}
                styles={(theme) => ({
                  root: {
                    '&:hover': { backgroundColor: theme.colors.gray[1] },
                  },
                })}
              >
                <Text size="sm" fw={600} c="azul.7">
                  [+ Create new {entityNoun ? entityNoun.replace(/s$/, '') : 'record'}]
                </Text>
              </UnstyledButton>
            )}
            {/* Filter pinned options (e.g. All / General) above the live
                search; selecting one commits its sentinel value. */}
            {pinnedOptions?.map((opt) => (
              <UnstyledButton
                key={opt.value}
                onMouseDown={(e) => { e.preventDefault(); commit(opt.value); }}
                style={{ padding: '6px 10px', borderRadius: 4, width: '100%', textAlign: 'left' }}
                styles={(theme) => ({
                  root: { '&:hover': { backgroundColor: theme.colors.gray[1] } },
                })}
              >
                <Text size="sm" fw={500}>{opt.label}</Text>
              </UnstyledButton>
            ))}
            {pinnedOptions && pinnedOptions.length > 0 && (
              <div style={{
                borderBottom: '1px solid var(--mantine-color-gray-2)',
                marginBottom: 4,
              }} />
            )}
            {searchQ.isLoading && (
              <div style={{ padding: 8, display: 'flex', alignItems: 'center', gap: 8 }}>
                <Loader size="xs" /> <Text size="sm" c="dimmed">Searching…</Text>
              </div>
            )}
            {!searchQ.isLoading && items.length === 0 && (
              <div style={{ padding: '8px 10px', textAlign: 'center' }}>
                <Text size="sm" c="dimmed">
                  {searchInput ? 'No matches' : `No ${entityNoun ?? 'entries'} yet`}
                </Text>
                {createPath && (
                  <Text size="xs" c="dimmed" mt={4}>
                    <a href={createPath} target="_blank" rel="noopener noreferrer">
                      Create one in a new tab →
                    </a>
                  </Text>
                )}
              </div>
            )}
            {items.map((row) => (
              <UnstyledButton
                key={row.id}
                onMouseDown={(e) => {
                  // preventDefault on mousedown is what stops the input from
                  // losing focus before the click registers.
                  e.preventDefault();
                  commit(row.id);
                }}
                style={{
                  padding: '6px 10px',
                  borderRadius: 4,
                  width: '100%',
                  textAlign: 'left',
                }}
                styles={(theme) => ({
                  root: {
                    '&:hover': { backgroundColor: theme.colors.gray[1] },
                  },
                })}
              >
                <Text size="sm" fw={500}>{formatLabel(row)}</Text>
                {formatDetail && formatDetail(row) && (
                  <Text size="xs" c="dimmed">{formatDetail(row)}</Text>
                )}
              </UnstyledButton>
            ))}
            {searchQ.data && searchQ.data.total > items.length && (
              <Text size="xs" c="dimmed" ta="center" p="xs">
                Showing {items.length} of {searchQ.data.total} — refine search to narrow.
              </Text>
            )}
          </Stack>
        </Paper>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Specialised wrappers — drop-in replacements
// ---------------------------------------------------------------------------
type PickerCommon = Omit<EntityPickerProps<never>, 'search' | 'getById' | 'formatLabel' | 'formatDetail'>;

export function PersonPicker(p: PickerCommon) {
  return (
    <EntityPicker
      createPath="/persons/new"
      entityNoun="persons"
      {...p}
      search={(q) => personsApi.list({ q: q || undefined, limit: 25 })}
      getById={(id) => personsApi.get(id)}
      formatLabel={(row) => row.display_name || `(unnamed person ${row.id.slice(0, 8)})`}
      formatDetail={(row) => row.email || (row.kind === 'LEGAL' ? 'Legal entity' : 'Natural person')}
    />
  );
}

export function AgreementPicker(p: PickerCommon & { statusFilter?: LifecycleStatus[] }) {
  const { statusFilter, ...rest } = p;
  return (
    <EntityPicker
      createPath="/agreements/new"
      entityNoun="agreements"
      {...rest}
      search={(q) => agreementsApi.list({
        q: q || undefined, limit: 25, ...(statusFilter ? { status: statusFilter } : {}),
      })}
      getById={(id) => agreementsApi.get(id)}
      formatLabel={(row) => `${row.code_1} — ${row.client_mnemonic}`}
      formatDetail={(row) => `${row.client_type} · ${row.status}`}
    />
  );
}

/**
 * Phase A2.b (2026-06-09): picker for Insurance Policies. Used on the New
 * Account form when the user chooses "Policy (Indirect)" as the parent
 * type. Filtered to Active policies (per A2.a backend rule that Indirect
 * accounts can only be created under Active policies).
 */
export function InsurancePolicyPicker(p: PickerCommon) {
  return (
    <EntityPicker
      createPath="/account-insurance/new"
      entityNoun="insurance policies"
      {...p}
      search={(q) => accountInsuranceApi.list({
        q: q || undefined,
        status: ['Active'],
        limit: 25,
      })}
      getById={(id) => accountInsuranceApi.get(id)}
      formatLabel={(row) => `${row.insurer} — ${row.policy_number}`}
      formatDetail={(row) => row.fund_type || undefined}
    />
  );
}

export function AccountPicker(p: PickerCommon & { agreementId?: UUID }) {
  const { agreementId, ...rest } = p;
  return (
    <EntityPicker
      createPath="/accounts/new"
      entityNoun="accounts"
      {...rest}
      search={(q) => accountsApi.list({
        q: q || undefined,
        agreement_id: agreementId,
        limit: 25,
      })}
      getById={(id) => accountsApi.get(id)}
      formatLabel={(row) => row.internal_num
        ? `${row.bank_name} — ${row.internal_num}`
        : row.bank_name}
      formatDetail={(row) => row.iban || undefined}
    />
  );
}

/**
 * AgreementPartyPicker — used for `related_party_id` on agreement_parties.
 * Filtered to the parent agreement so you can only point at parties on the
 * same agreement.
 */
export function AgreementPartyPicker(p: PickerCommon & { agreementId: UUID }) {
  const { agreementId, ...rest } = p;
  return (
    <EntityPicker
      entityNoun="parties on this agreement"
      {...rest}
      search={() => agreementPartiesApi.list({
        agreement_id: agreementId,
        limit: 25,
      })}
      getById={(id) => agreementPartiesApi.get(id)}
      formatLabel={(row) => `Party ${row.id.slice(0, 8)} — ${row.role}`}
      formatDetail={(row) => row.notes || undefined}
    />
  );
}
